'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Logomark } from '@/components/brand'
import { Icons } from '@/components/ui/icons'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { AUTO_NAME, isNavItemActive } from '@/lib/navigation'
import { useVisibleNavSections } from './Sidebar'

interface NavLinkProps {
  href: string
  icon: React.ElementType
  children: React.ReactNode
  onClick?: () => void
}

function NavLink({ href, icon: Icon, children, onClick }: NavLinkProps) {
  const pathname = usePathname()
  const isActive = isNavItemActive(pathname, href)

  return (
    <Link
      href={href}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex items-center gap-3 rounded-xl px-4 py-3',
        'text-base font-medium transition-all duration-200',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-black/[0.04] hover:text-foreground'
      )}
    >
      <Icon
        strokeWidth={1.5}
        aria-hidden='true'
        className={cn('h-5 w-5 shrink-0', isActive ? 'text-primary-foreground' : 'text-muted-foreground')}
      />
      <span>{children}</span>
    </Link>
  )
}

interface MobileSidebarProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Phone-width navigation. Same sections, labels, role filtering and folded
 * sections as the desktop rail (plan 03 CHG-18), from lib/navigation.ts.
 */
export function MobileSidebar({ isOpen, onClose }: MobileSidebarProps) {
  const sections = useVisibleNavSections()
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side='left' className='w-72 p-0 md:hidden'>
        {/* Header */}
        <SheetHeader className='flex h-20 flex-row items-center justify-between border-b border-black/[0.04] px-4'>
          <Link href='/' className='flex items-center gap-3' onClick={onClose}>
            <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-primary shadow-soft'>
              <Logomark variant='light' size={24} />
            </div>
            <div className='flex flex-col'>
              <SheetTitle className='font-display text-lg font-bold tracking-tight text-foreground'>
                Supervity
              </SheetTitle>
              <span className='text-xs font-medium uppercase leading-tight tracking-widest text-muted-foreground'>
                {AUTO_NAME}
                <br />
                Command Center
              </span>
            </div>
          </Link>
        </SheetHeader>

        {/* Navigation */}
        <nav className='flex-1 overflow-y-auto p-4' aria-label='Mobile navigation'>
          {sections.map((section, sectionIndex) => {
            const isOpen = openSections[section.title] ?? !section.collapsedByDefault
            const listId = `mobile-nav-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`
            return (
              <div key={section.title} className={cn(sectionIndex > 0 && 'mt-6')}>
                {section.collapsedByDefault ? (
                  <button
                    type='button'
                    onClick={() => setOpenSections((prev) => ({ ...prev, [section.title]: !isOpen }))}
                    aria-expanded={isOpen}
                    aria-controls={listId}
                    className='mb-2 flex w-full items-center gap-1.5 px-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground'
                  >
                    <span>{section.title}</span>
                    <Icons.chevronDown
                      aria-hidden='true'
                      className={cn('h-3.5 w-3.5 transition-transform duration-200', !isOpen && '-rotate-90')}
                    />
                  </button>
                ) : (
                  <p className='mb-2 px-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground'>
                    {section.title}
                  </p>
                )}
                {isOpen && (
                  <div id={listId} className='space-y-1'>
                    {section.items.map((item) => (
                      <NavLink key={item.href} href={item.href} icon={item.icon} onClick={onClose}>
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </nav>
      </SheetContent>
    </Sheet>
  )
}
